import { create } from 'zustand';
import api from '../services/api';
import { getTutorialSteps } from '../config/tutorialSteps';
import { logger } from '../utils/logger';
import { isNative } from '../utils/platform';
import { navigateTo } from '../services/navigation';
import * as tokenStore from '../services/tokenStore';

// Simple JWT decoder (avoiding external dependency requirement for now)
const decodedJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

// Check if a stored token is expired (with 30s buffer)
const getValidToken = () => {
  const token = tokenStore.getItem('authToken');
  if (!token) return null;
  const decoded = decodedJWT(token);
  if (!decoded?.exp) return token; // no exp claim, keep it
  if (decoded.exp * 1000 < Date.now() - 30000) {
    // Token expired — clear auth from localStorage
    tokenStore.removeItem('authToken');
    tokenStore.removeItem('user');
    tokenStore.removeItem('centerId');
    tokenStore.removeItem('centerType');
    tokenStore.removeItem('learningSubType');
    return null;
  }
  return token;
};

const initialToken = getValidToken();

const loadMembershipsFromStorage = () => {
  try {
    const raw = tokenStore.getItem('memberships');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/**
 * Read the persisted session into the shape the store holds it in.
 *
 * ONE reader for two call sites: the module-load initializer below, and
 * `bootstrap()` (native, after tokenStore.hydrate() resolves). They used to be
 * two hand-maintained field lists, which is exactly how `learningSubType` went
 * missing from the native cold-launch path — a Quran center silently lost its
 * Hifz labelling until the next login. Add a session field HERE and both paths
 * get it.
 */
const readSessionFromStorage = (token) => {
  if (!token) {
    return {
      user: null,
      token: null,
      centerId: null,
      centerType: 'learning',
      learningSubType: 'teaching',
      memberships: [],
      defaultCenterId: null,
    };
  }
  let user = null;
  try { user = JSON.parse(tokenStore.getItem('user')) || null; } catch { user = null; }
  return {
    user,
    token,
    centerId: tokenStore.getItem('centerId') || null,
    centerType: tokenStore.getItem('centerType') || 'learning',
    // Sub-type WITHIN a learning center: 'teaching' (default) | 'quran' (Hifz / memorization center).
    // Only meaningful when centerType === 'learning'. Therapy / hair_transplant ignore it entirely.
    learningSubType: tokenStore.getItem('learningSubType') || 'teaching',
    memberships: loadMembershipsFromStorage(),
    defaultCenterId: tokenStore.getItem('defaultCenterId') || null,
  };
};

const useAuthStore = create((set, get) => ({
  ...readSessionFromStorage(initialToken),
  isLoading: false,
  error: null,

  // Tutorial state
  showTutorial: false,
  tutorialSteps: [],
  tutorialReady: false,

  setUser: (user, token) => {
    logger.auth('[AuthStore] setUser called with:', { user, token: token ? `${token.substring(0, 20)}...` : 'null' });
    if (user && token) {
      logger.auth('[AuthStore] Storing user and token in localStorage');
      tokenStore.setItem('user', JSON.stringify(user));
      tokenStore.setItem('authToken', token);
      logger.auth('[AuthStore] Token stored, length:', token.length);

      // Extract centerId from JWT token
      const decoded = decodedJWT(token);
      logger.auth('[AuthStore] Decoded JWT:', decoded);
      const centerId = decoded?.activeCenterId || decoded?.centerId || null;
      tokenStore.setItem('centerId', centerId || '');
      logger.auth('[AuthStore] CenterId set to:', centerId);

      // Extract centerType from user object (comes from login response)
      const centerType = user?.centerType || 'learning';
      tokenStore.setItem('centerType', centerType);
      logger.auth('[AuthStore] CenterType set to:', centerType);

      // Learning sub-type (teaching | quran). Backend returns it from the center doc.
      const learningSubType = user?.learningSubType || 'teaching';
      tokenStore.setItem('learningSubType', learningSubType);

      // Memberships: prefer enriched list from login response; fall back to JWT compact list.
      const memberships = Array.isArray(user?.memberships) && user.memberships.length > 0
        ? user.memberships
        : (Array.isArray(decoded?.memberships) ? decoded.memberships : []);
      tokenStore.setItem('memberships', JSON.stringify(memberships));

      const defaultCenterId = user?.defaultCenterId || centerId || null;
      tokenStore.setItem('defaultCenterId', defaultCenterId || '');

      // Check if tutorial should be shown
      const shouldShowTutorial =
        user?.onboarding?.hasCompletedTutorial === false &&
        user?.onboarding?.skipTutorialOnLogin !== true;

      // Load tutorial steps based on user role and center type
      const tutorialSteps = shouldShowTutorial
        ? getTutorialSteps(user?.role, centerType)
        : [];

      set({
        user,
        token,
        centerId,
        centerType,
        learningSubType,
        memberships,
        defaultCenterId,
        error: null,
        showTutorial: shouldShowTutorial,
        tutorialSteps,
        tutorialReady: shouldShowTutorial && tutorialSteps.length > 0
      });
      logger.auth('[AuthStore] State updated with tutorial settings:', {
        shouldShowTutorial,
        tutorialStepsCount: tutorialSteps.length,
        membershipsCount: memberships.length
      });
    } else {
      logger.auth('[AuthStore] user or token is missing, clearing auth');
      set({ user, token, centerId: null, centerType: 'learning', learningSubType: 'teaching', memberships: [], defaultCenterId: null, error: null });
    }
  },

  /**
   * Switch to a different center. Calls POST /api/auth/switch-center, replaces the stored JWT,
   * then does a full page reload so every component re-reads state fresh (decision #6 in the
   * multi-center plan).
   */
  switchCenter: async (centerId) => {
    const state = get();
    if (!centerId || centerId === state.centerId) return;
    try {
      set({ isLoading: true, error: null });
      const response = await api.post('/auth/switch-center', { centerId });
      // api interceptor returns response.data — `token` and `user` are at the root.
      if (!response?.token || !response?.user) {
        throw new Error('Invalid switch-center response');
      }
      // Persist — don't set state first, we're about to reload.
      tokenStore.setItem('user', JSON.stringify(response.user));
      tokenStore.setItem('authToken', response.token);
      tokenStore.setItem('centerId', response.user.centerId || '');
      tokenStore.setItem('centerType', response.user.centerType || 'learning');
      tokenStore.setItem('learningSubType', response.user.learningSubType || 'teaching');
      tokenStore.setItem('memberships', JSON.stringify(response.user.memberships || []));
      tokenStore.setItem('defaultCenterId', response.user.defaultCenterId || '');
      if (isNative) {
        // No hard-reload server on native: update in-memory state directly so
        // the new tenant scope is reflected, then soft-navigate. (Components
        // re-mount on the route change; the storage writes above keep
        // boot-time reads correct.)
        set({
          user: response.user,
          token: response.token,
          centerId: response.user.centerId || null,
          centerType: response.user.centerType || 'learning',
          learningSubType: response.user.learningSubType || 'teaching',
          memberships: response.user.memberships || [],
          defaultCenterId: response.user.defaultCenterId || null,
          isLoading: false,
        });
        navigateTo('/dashboard', { replace: true });
      } else {
        // Hard reload — blows away all in-memory caches / queries / subscriptions
        // so the new tenant scope is picked up everywhere.
        window.location.assign('/dashboard');
      }
    } catch (error) {
      set({ isLoading: false, error: error?.message || 'Failed to switch center' });
      logger.auth('[AuthStore] switchCenter failed', error);
      throw error;
    }
  },

  /**
   * Mark a center as the user's default (the one to land on at next login). Star icon action.
   */
  /**
   * Re-read this session's centers from the server.
   *
   * `memberships` was only ever written at login/switch, so anything that
   * changed server-side — being added to another center, or having one revoked
   * — stayed invisible until the user happened to sign out and back in. That is
   * exactly the confusing part of "I was added to a center but can't see it".
   *
   * GET /auth/verify already returns the enriched list (and re-validates the
   * active center), so this just syncs what we store. Silent and best-effort:
   * a failure here must never interrupt an otherwise working session, and a
   * genuinely invalid token is handled by the api 401 interceptor.
   */
  refreshMemberships: async () => {
    const state = get();
    if (!state.token) return;
    try {
      const response = await api.get('/auth/verify');
      const u = response?.user || response?.data?.user;
      if (!u || !Array.isArray(u.memberships)) return;

      tokenStore.setItem('memberships', JSON.stringify(u.memberships));
      if (u.defaultCenterId) tokenStore.setItem('defaultCenterId', u.defaultCenterId);

      const mergedUser = state.user ? { ...state.user, memberships: u.memberships } : state.user;
      if (mergedUser) tokenStore.setItem('user', JSON.stringify(mergedUser));

      set({
        memberships: u.memberships,
        defaultCenterId: u.defaultCenterId || state.defaultCenterId,
        user: mergedUser,
      });
    } catch (error) {
      logger.auth('[AuthStore] refreshMemberships failed (non-fatal)', error);
    }
  },

  setDefaultCenter: async (centerId) => {
    if (!centerId) return;
    try {
      const response = await api.put('/auth/default-center', { centerId });
      const newDefault = response?.defaultCenterId || centerId;
      tokenStore.setItem('defaultCenterId', newDefault);
      const state = get();
      const updatedUser = state.user ? { ...state.user, defaultCenterId: newDefault } : state.user;
      if (updatedUser) tokenStore.setItem('user', JSON.stringify(updatedUser));
      set({ defaultCenterId: newDefault, user: updatedUser });
    } catch (error) {
      logger.auth('[AuthStore] setDefaultCenter failed', error);
      throw error;
    }
  },

  setCenterType: (centerType) => {
    tokenStore.setItem('centerType', centerType);
    set({ centerType });
  },

  // Switch the learning sub-type (teaching <-> quran <-> caffe <-> sport) in-place. The
  // Settings page calls this after the backend persists center.learningSubType
  // so the whole UI (terminology, Hifz / Tournament features) reacts without a
  // full reload.
  setLearningSubType: (learningSubType) => {
    const value = ['quran', 'caffe', 'sport'].includes(learningSubType) ? learningSubType : 'teaching';
    tokenStore.setItem('learningSubType', value);
    const state = get();
    const updatedUser = state.user ? { ...state.user, learningSubType: value } : state.user;
    if (updatedUser) tokenStore.setItem('user', JSON.stringify(updatedUser));
    set({ learningSubType: value, user: updatedUser });
  },

  logout: () => {
    // Native: release this device's push registration first — while the token
    // is still valid — so the next person to sign in here doesn't inherit the
    // previous user's notifications. Fire-and-forget; never blocks sign-out.
    if (isNative) {
      import('../native/push')
        .then((m) => m.unregisterPush())
        .catch(() => { /* best effort */ });
    }
    tokenStore.removeItem('user');
    tokenStore.removeItem('authToken');
    tokenStore.removeItem('centerId');
    tokenStore.removeItem('centerType');
    tokenStore.removeItem('learningSubType');
    tokenStore.removeItem('memberships');
    tokenStore.removeItem('defaultCenterId');
    set({ user: null, token: null, centerId: null, centerType: 'learning', learningSubType: 'teaching', memberships: [], defaultCenterId: null });
  },

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  isAuthenticated: () => {
    const state = useAuthStore.getState();
    return !!state.token && !!state.user;
  },

  /**
   * Re-read auth state from storage into the store. Used on native after
   * tokenStore.hydrate() loads @capacitor/preferences (the module-load init
   * runs before that async hydrate, so the store starts logged-out and this
   * fills it in before first render). No-op when there's no valid token.
   */
  bootstrap: () => {
    const token = getValidToken();
    if (!token) return;
    set(readSessionFromStorage(token));
  },

  // Tutorial actions
  setShowTutorial: (show) => set({ showTutorial: show }),

  setTutorialSteps: (steps) => set({ tutorialSteps: steps, tutorialReady: true }),

  completeTutorial: async (version) => {
    const state = get();
    try {
      await api.put(`/users/${state.user.id}/onboarding`, {
        action: 'complete',
        tutorialVersion: version
      });

      // Update local user state
      const updatedUser = {
        ...state.user,
        onboarding: {
          ...state.user.onboarding,
          hasCompletedTutorial: true,
          tutorialVersion: version
        }
      };

      tokenStore.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser, showTutorial: false, tutorialReady: false });
      logger.auth('[AuthStore] Tutorial completed successfully');
    } catch (error) {
      console.error('[AuthStore] Error completing tutorial:', error);
    }
  },

  skipTutorial: async (permanently = false) => {
    const state = get();
    try {
      if (permanently) {
        await api.put(`/users/${state.user.id}/onboarding`, {
          action: 'skip',
          skipTutorialOnLogin: true
        });

        const updatedUser = {
          ...state.user,
          onboarding: {
            ...state.user.onboarding,
            skipTutorialOnLogin: true
          }
        };

        tokenStore.setItem('user', JSON.stringify(updatedUser));
        set({ user: updatedUser, showTutorial: false, tutorialReady: false });
        logger.auth('[AuthStore] Tutorial skipped permanently');
      } else {
        // Just hide for this session
        set({ showTutorial: false, tutorialReady: false });
        logger.auth('[AuthStore] Tutorial skipped for this session');
      }
    } catch (error) {
      console.error('[AuthStore] Error skipping tutorial:', error);
    }
  },

  restartTutorial: async () => {
    const state = get();
    try {
      await api.put(`/users/${state.user.id}/onboarding`, {
        action: 'restart'
      });

      // Load tutorial steps
      const tutorialSteps = getTutorialSteps(state.user?.role, state.centerType);

      set({
        showTutorial: true,
        tutorialSteps,
        tutorialReady: tutorialSteps.length > 0
      });
      logger.auth('[AuthStore] Tutorial restarted');
    } catch (error) {
      console.error('[AuthStore] Error restarting tutorial:', error);
    }
  }
}));

// Dev-only debug hook: expose the Zustand store on `window.__authStore` so we
// can visually verify protected pages (e.g. the new EntityDetail shell) from
// the browser preview console without round-tripping the backend.
// Stripped from production builds — `process.env.NODE_ENV` is dead-code
// eliminated by webpack/CRA in production.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.__authStore = useAuthStore;
}

export default useAuthStore;
